import Pass from "_src/render_graph/pass";
import resourceManager from "_src/render_graph/resource_manager";
import FrameBuffer from "_src/engine/framebuffer";
import {pixelBufferType, attachmentId, RBIntFormat, drawMode} from "_src/engine/constants";
import GL from "_src/engine/gl";
import renderer from "_src/engine/renderer";
export default class CompareDrawPass extends Pass {

  constructor(attachmentsStorage, camera) {
    super(attachmentsStorage);
    this.init(camera);

    this.enableTriangleIDTexture = false;
    this._leftObject = null;
    this._rightObject = null;
    this._leftSceneDrawMode = drawMode.TRIANGLES;
    this._rightSceneDrawMode = drawMode.TRIANGLES;
    this._leftSceneTextureUsage = true;
    this._rightSceneTextureUsage = true;
    this._shadersCache = new Map();
  }

  init(camera) {
    this._addScene(camera);
    this._addScene(camera);
    const leftInterfaceScene = this._addScene(camera);
    const rightInterfaceScene = this._addScene(camera);

    leftInterfaceScene.clearedColor = false;
    leftInterfaceScene.clearedDepth = false;
    leftInterfaceScene.clearedStencil = false;

    rightInterfaceScene.clearedColor = false;
    rightInterfaceScene.clearedDepth = false;
    rightInterfaceScene.clearedStencil = false;
  }

  update() {
    if (!this.enabled) return;

    const leftModelScene = this.getScenes()[0];
    const rightModelScene = this.getScenes()[1];
    const leftInterfaceScene = this.getScenes()[2];
    const rightInterfaceScene = this.getScenes()[3];

    const width = leftModelScene.camera.viewport[2];
    const height = leftModelScene.camera.viewport[3];

    const {leftModelFramebuffer, rightModelFramebuffer, leftInterfaceFramebuffer, rightInterfaceFramebuffer} = this.initFramebuffer(width, height);
    leftModelScene.setFramebuffer(leftModelFramebuffer);
    rightModelScene.setFramebuffer(rightModelFramebuffer);
    leftInterfaceScene.setFramebuffer(leftInterfaceFramebuffer);
    rightInterfaceScene.setFramebuffer(rightInterfaceFramebuffer);
  }

  initFramebuffer(width, height) {
    const usedLeftModelTexture = this.attachmentsStorage[attachmentId.COMPARE_LEFT_TEXTURE];
    const usedRightModelTexture = this.attachmentsStorage[attachmentId.COMPARE_RIGHT_TEXTURE];
    const leftModelTexture = usedLeftModelTexture ? usedLeftModelTexture : resourceManager.initBuffer(pixelBufferType.T_RGBA, { width, height });
    const rightModelTexture = usedRightModelTexture ? usedRightModelTexture : resourceManager.initBuffer(pixelBufferType.T_RGBA, { width, height });
    const depthFormat = GL.checkWebGL2() ? RBIntFormat.D24S8 : RBIntFormat.DS;
    const leftModelDepthStencil = resourceManager.initBuffer(pixelBufferType.RB_DEPTH, { width, height, format: depthFormat });
    const rightModelDepthStencil = resourceManager.initBuffer(pixelBufferType.RB_DEPTH, { width, height, format: depthFormat });
    this.attachmentsStorage[attachmentId.COMPARE_LEFT_TEXTURE] = leftModelTexture;
    this.attachmentsStorage[attachmentId.COMPARE_RIGHT_TEXTURE] = rightModelTexture;
    const leftColorAttachments = [leftModelTexture];
    const rightColorAttachments = [rightModelTexture];

    if (this.enableTriangleIDTexture) {
      const leftTexture = this.attachmentsStorage[attachmentId.COMPARE_LEFT_TRIANGLE_ID_TEXTURE] ?
          this.attachmentsStorage[attachmentId.COMPARE_LEFT_TRIANGLE_ID_TEXTURE] :
          resourceManager.initBuffer(pixelBufferType.T_RGBA,{width, height});
      const rightTexture = this.attachmentsStorage[attachmentId.COMPARE_RIGHT_TRIANGLE_ID_TEXTURE] ?
          this.attachmentsStorage[attachmentId.COMPARE_RIGHT_TRIANGLE_ID_TEXTURE] :
          resourceManager.initBuffer(pixelBufferType.T_RGBA,{width, height});

      this.attachmentsStorage[attachmentId.COMPARE_LEFT_TRIANGLE_ID_TEXTURE] = leftTexture;
      this.attachmentsStorage[attachmentId.COMPARE_RIGHT_TRIANGLE_ID_TEXTURE] = rightTexture;

      leftColorAttachments.push(leftTexture);
      rightColorAttachments.push(rightTexture);
    }

    const leftModelFramebuffer = new FrameBuffer(leftColorAttachments, leftModelDepthStencil);
    const rightModelFramebuffer = new FrameBuffer(rightColorAttachments, rightModelDepthStencil);
    const leftInterfaceFramebuffer = new FrameBuffer([leftModelTexture], leftModelDepthStencil);
    const rightInterfaceFramebuffer = new FrameBuffer([rightModelTexture], rightModelDepthStencil);

    return {leftModelFramebuffer, rightModelFramebuffer, leftInterfaceFramebuffer, rightInterfaceFramebuffer};
  }

  setObjects(leftObject, rightObject) {
    const leftModelScene = this.getScenes()[0];
    const rightModelScene = this.getScenes()[1];
    const leftInterfaceScene = this.getScenes()[2];
    const rightInterfaceScene = this.getScenes()[3];
    leftModelScene.removeChildren();
    rightModelScene.removeChildren();
    leftInterfaceScene.removeChildren();
    rightInterfaceScene.removeChildren();

    if (leftObject) {
      leftModelScene.add(leftObject.model);
      if (leftObject.edgesModel) leftInterfaceScene.add(leftObject.edgesModel);
    }
    if (rightObject) {
      rightModelScene.add(rightObject.model);
      if (rightObject.edgesModel) rightInterfaceScene.add(rightObject.edgesModel);
    }

    this._leftObject = leftObject;
    this._rightObject = rightObject;
  }

  disable() {
    super.disable();
    this.attachmentsStorage[attachmentId.COMPARE_LEFT_TEXTURE] = null;
    this.attachmentsStorage[attachmentId.COMPARE_RIGHT_TEXTURE] = null;
    this.attachmentsStorage[attachmentId.COMPARE_LEFT_TRIANGLE_ID_TEXTURE] = null;
    this.attachmentsStorage[attachmentId.COMPARE_RIGHT_TRIANGLE_ID_TEXTURE] = null;
  }

  setRenderSettings(leftSceneDrawMode, rightSceneDrawMode, leftTextureUsage, rightTextureUsage) {
    this._leftSceneDrawMode = leftSceneDrawMode;
    this._rightSceneDrawMode = rightSceneDrawMode;
    this._leftSceneTextureUsage = leftTextureUsage;
    this._rightSceneTextureUsage = rightTextureUsage;
    this._shadersCache.clear();
  }

  render(skipChunkTests) {
    this._validateCache();

    for (let i = 0; i < this.scenes.length; ++i) {

      const scene = this.scenes[i];

      if (scene.getEnabled()) {
        this._configureShader(i);

        renderer.renderScene(scene, skipChunkTests);

        this._updateCache(i);
      }
    }
  }

  _configureShader(i) {
    if (i===0) _setObjectRenderSettings(this._leftObject, this._shadersCache, this._leftSceneDrawMode, this._leftSceneTextureUsage);
    if (i===1) _setObjectRenderSettings(this._rightObject, this._shadersCache, this._rightSceneDrawMode, this._rightSceneTextureUsage);
  }

  _updateCache(i) {
    if (this._leftObject !== this._rightObject) return;

    if (i===0) _updateShaderCache(this._leftObject, this._shadersCache, this._leftSceneDrawMode, this._leftSceneTextureUsage);
    if (i===1) _updateShaderCache(this._rightObject, this._shadersCache, this._rightSceneDrawMode, this._rightSceneTextureUsage);
  }

  _validateCache() {
    if (this._leftObject !== this._rightObject) return;

    if (this._leftObject.model.shader.needsUpdate || this._rightObject.model.shader.needsUpdate || _isObjectDirty(this._leftObject) || _isObjectDirty(this._rightObject)) {
      this._shadersCache.clear();
    }
  }

}

function _setObjectRenderSettings(object, shadersCache, drawMode, textureUsage) {
  if (object) {
    const hash = _settingsHash(drawMode, textureUsage);
    const shaderData = shadersCache.get(hash);
    if (shaderData) {
      object.model.shader.program = shaderData.program;
      for (const [uniName, uniLocation] of Object.entries(shaderData.uniformLocations)) {
        object.model.shader.uniforms[uniName].location = uniLocation;
      }
      for (const chunk of object.frames[0].model.chunks) {
        const chunkProperties = object.frames[0].model._chunkProperties.get(chunk);
        if (!shaderData.chunkAttributes[chunk.getID()] || !shaderData.chunkAttributes[chunk.getID()].attributesMap.length) continue;

        chunkProperties.activeShaderProgram = shaderData.program;
        chunkProperties.attributesMap = shaderData.chunkAttributes[chunk.getID()].attributesMap;
        chunkProperties.vao = shaderData.chunkAttributes[chunk.getID()].vao;
      }
    } else {
      object.model.shader.setDrawMode(drawMode);
      object.updateTextureUsage(textureUsage);
    }

    object.frames[0].model.setDrawMode(drawMode);
    object.updateColorSpace(textureUsage);
  }
}

//This is a hack to avoid resources recreation in case of comparing the same object with different render settings
function _updateShaderCache(object, shadersCache, drawMode, textureUsage) {
  if (object) {
    const hash = _settingsHash(drawMode, textureUsage);
    if (!shadersCache.has(hash)) {
      const uniformLocations = {};
      for (const [uniName, uni] of Object.entries(object.model.shader.uniforms)) {
        uniformLocations[uniName] = uni.location;
      }
      const chunkAttributes = {};
      for (const chunk of object.frames[0].model.chunks) {
        const chunkProperties = object.frames[0].model._chunkProperties.get(chunk);
        chunkAttributes[chunk.getID()] = {
          attributesMap: chunkProperties.attributesMap,
          vao: chunkProperties.vao,
        };
      }

      const cachedData = {
        program: object.model.shader.program,
        uniformLocations: uniformLocations,
        chunkAttributes: chunkAttributes,
      }
      shadersCache.set(hash, cachedData);
    }
  }
}

function _settingsHash(drawMode, textureUsage) {
  return drawMode.toString() + textureUsage.toString();
}

function _isObjectDirty(object) {
  for (const chunk of object.frames[0].model.chunks) {
    if (chunk._dirtyAttributes.size > 0) return true;
  }

  return false;
}
